/**
 * El eje del cuadro de muelles: horas, no días.
 *
 * Vive aparte de `planningTimeTracks.ts` a propósito. Aquel fichero es el Gantt
 * de Planificación Avanzada y sus tres builders comparten un modelo lineal en
 * días; éstos traen un eje **a trozos** que no se parece a ellos. Juntarlos
 * dejaría un fichero con dos geometrías distintas dentro, que es justo lo que no
 * queremos. Lo que sí es común —`TimeTrack`, `TrackColumn`, `stripDot`, `pad2`—
 * se importa.
 *
 * ## Por qué la semana va a trozos
 *
 * Un eje semanal continuo, de lunes 00:00 a domingo 24:00, dedicaría el 58 % del
 * carril a horas en las que el muelle está cerrado, y dejaría un camión de
 * noventa minutos ocupando el 0,9 % del ancho: invisible. Así que el eje semanal
 * **empalma siete bloques de jornada y descarta las noches**. El span pasa a ser
 * de 98 horas operativas y ese mismo camión mide el 1,53 %.
 *
 * ## Por qué es inmune al cambio de hora
 *
 * Cada bloque se construye con `new Date(y, m, d, 6)` y `new Date(y, m, d, 20)`
 * en hora LOCAL, así que mide catorce horas reales los 365 días del año: el salto
 * de horario en España es a las 02:00/03:00, fuera de la jornada. Un eje continuo
 * tendría una semana de 167 horas y otra de 169, y todas las barras se
 * desplazarían esa semana sin que nadie supiera por qué.
 */
import type { TimeTrack, TrackColumn, TrackI18n } from '@/utils/planningTimeTracks'
import { stripDot } from '@/utils/planningTimeTracks'
import { MS_HORA, MS_MIN, ahoraEnMs, posEnMs } from '@/utils/planningDateRanges'
import { JORNADA_INICIO, JORNADA_FIN, SLOT_MINUTOS } from '@/config/dockSchedule'

export type DockVista = 'dia' | 'semana'

export interface Jornada {
  inicio: number
  fin: number
}

export const JORNADA_POR_DEFECTO: Jornada = { inicio: JORNADA_INICIO, fin: JORNADA_FIN }

/** Un trozo de eje: el rato que un día concreto el muelle está abierto.
 *  `offsetMs` es dónde empieza ese bloque dentro del carril, ya sin las noches. */
interface BloqueJornada {
  iniMs: number
  finMs: number
  offsetMs: number
  dia: Date
}

export interface DockTrack extends TimeTrack<DockVista> {
  granularidad: 'hora'
  jornada: Jornada
  /** Los días que cubre el eje: uno en vista día, siete en semana. */
  dias: Date[]
  /** Inverso de `pos`: dado un % del carril, qué instante es. Lo necesita el
   *  hueco clicable («reservar aquí») y, más adelante, el drop del arrastre.
   *  Redondea al slot configurado. */
  at: (pct: number) => Date
  /** «Ahora» cae fuera de la jornada: antes de las seis, después de las ocho, o
   *  en un día que no está en pantalla. La línea **se sigue pintando** —en el
   *  borde del bloque y discontinua—, porque esconderla haría creer que hoy no
   *  está en el eje. */
  ahoraFueraJornada: boolean
  /** Horas operativas que abarca el eje: 14 en el día, 98 en la semana. */
  horasOperativas: number
}

function inicioDeDia(d: Date, hora: number): Date {
  const x = new Date(d)
  x.setHours(hora, 0, 0, 0)
  return x
}

function construirBloques(dias: Date[], jornada: Jornada): BloqueJornada[] {
  let offset = 0
  return dias.map((d) => {
    const ini = inicioDeDia(d, jornada.inicio).getTime()
    const fin = inicioDeDia(d, jornada.fin).getTime()
    const bloque: BloqueJornada = { iniMs: ini, finMs: fin, offsetMs: offset, dia: d }
    offset += fin - ini
    return bloque
  })
}

/** Proyecta un instante al carril «sin noches», o `null` si cae en una noche o
 *  fuera del rango. */
function aCarril(ms: number, bloques: BloqueJornada[]): number | null {
  for (const b of bloques) {
    if (ms >= b.iniMs && ms <= b.finMs) return b.offsetMs + (ms - b.iniMs)
  }
  return null
}

/** Proyecta pero sin exigir que caiga dentro: recorta al bloque más cercano.
 *  Es lo que permite pintar una barra que empieza antes de abrir el muelle. */
function aCarrilRecortado(ms: number, bloques: BloqueJornada[]): number {
  const primero = bloques[0]
  const ultimo = bloques[bloques.length - 1]
  if (ms <= primero.iniMs) return 0
  if (ms >= ultimo.finMs) return ultimo.offsetMs + (ultimo.finMs - ultimo.iniMs)
  for (const b of bloques) {
    if (ms < b.iniMs) return b.offsetMs           // cae en la noche anterior
    if (ms <= b.finMs) return b.offsetMs + (ms - b.iniMs)
  }
  return ultimo.offsetMs + (ultimo.finMs - ultimo.iniMs)
}

function construirTrack(
  vista: DockVista,
  dias: Date[],
  columns: TrackColumn[],
  boundaries: number[],
  jornada: Jornada,
  label: string,
  exportStamp: string,
  minLabelPct: number,
  anchoMin: number,
  secondaryStrong: boolean,
  ahora: number,
): DockTrack {
  const bloques = construirBloques(dias, jornada)
  const spanMs = bloques.reduce((acc, b) => acc + (b.finMs - b.iniMs), 0)
  const primero = bloques[0]
  const ultimo = bloques[bloques.length - 1]

  const pos = (start: Date | null, end: Date | null) => {
    if (!start || !end) return null
    const s = start.getTime()
    const e = end.getTime()
    if (e <= primero.iniMs || s >= ultimo.finMs) return null
    const left = aCarrilRecortado(s, bloques)
    const right = aCarrilRecortado(e, bloques)
    if (right <= left) return null
    return posEnMs(left, right, 0, spanMs, anchoMin)
  }

  const at = (pct: number): Date => {
    const dentro = Math.min(Math.max(pct, 0), 100)
    const enCarril = (dentro / 100) * spanMs
    let acumulado = 0
    for (const b of bloques) {
      const dura = b.finMs - b.iniMs
      if (enCarril <= acumulado + dura) {
        const crudo = b.iniMs + (enCarril - acumulado)
        const paso = SLOT_MINUTOS * MS_MIN
        return new Date(Math.round(crudo / paso) * paso)
      }
      acumulado += dura
    }
    return new Date(ultimo.finMs)
  }

  const enCarrilAhora = aCarril(ahora, bloques)
  const ahoraFueraJornada = enCarrilAhora === null
  let todayX: number | null = null
  if (enCarrilAhora !== null) {
    todayX = ahoraEnMs(0, spanMs, enCarrilAhora)
  } else if (ahora > primero.iniMs && ahora < ultimo.finMs) {
    // Está dentro del rango pero en una noche: se ancla al borde del bloque.
    todayX = (aCarrilRecortado(ahora, bloques) / spanMs) * 100
  }

  return {
    vista,
    granularidad: 'hora',
    rangeStart: new Date(primero.iniMs),
    rangeEnd: new Date(ultimo.finMs),
    columns,
    boundaries,
    pos,
    todayX,
    label,
    minLabelPct,
    exportStamp,
    secondaryStrong,
    jornada,
    dias,
    at,
    ahoraFueraJornada,
    horasOperativas: spanMs / MS_HORA,
  }
}

function diaConOffset(offset: number, hoy: Date): Date {
  const d = new Date(hoy)
  d.setDate(d.getDate() + offset)
  d.setHours(0, 0, 0, 0)
  return d
}

function lunesConOffset(offset: number, hoy: Date): Date {
  const d = new Date(hoy)
  d.setHours(0, 0, 0, 0)
  const dow = d.getDay()
  d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow) + offset * 7)
  return d
}

/** Vista de un día: catorce columnas de hora. */
export function buildDayTrack(
  offset: number,
  { locale }: TrackI18n,
  jornada: Jornada = JORNADA_POR_DEFECTO,
  ahora: number = Date.now(),
): DockTrack {
  const dia = diaConOffset(offset, new Date(ahora))
  const horas = jornada.fin - jornada.inicio
  const horaAhora = new Date(ahora)
  const esHoy = dia.toDateString() === horaAhora.toDateString()

  const columns: TrackColumn[] = Array.from({ length: horas }, (_, i) => ({
    weight: 1,
    primary: `${String(jornada.inicio + i).padStart(2, '0')}:00`,
    highlight: esHoy && horaAhora.getHours() === jornada.inicio + i,
  }))
  const boundaries = Array.from({ length: horas - 1 }, (_, i) => ((i + 1) / horas) * 100)

  const label = stripDot(
    dia.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'short' }),
  )
  const stamp = `${dia.getFullYear()}-${String(dia.getMonth() + 1).padStart(2, '0')}-${String(dia.getDate()).padStart(2, '0')}`

  return construirTrack(
    'dia', [dia], columns, boundaries, jornada, label, stamp,
    // Un camión de 90 min ocupa el 10,7 % del día: cabe de sobra el rótulo.
    5,
    // Suelo de ancho ≈ 12 min. Por debajo la tarjeta no se ve.
    1.5,
    false, ahora,
  )
}

/** Vista de semana: siete bloques de jornada empalmados, sin las noches. */
export function buildWeekHourTrack(
  offset: number,
  { locale }: TrackI18n,
  jornada: Jornada = JORNADA_POR_DEFECTO,
  ahora: number = Date.now(),
): DockTrack {
  const lunes = lunesConOffset(offset, new Date(ahora))
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(lunes)
    d.setDate(lunes.getDate() + i)
    d.setHours(0, 0, 0, 0)
    return d
  })
  const horas = jornada.fin - jornada.inicio
  const hoyStr = new Date(ahora).toDateString()

  const columns: TrackColumn[] = dias.map((d) => ({
    // Todas las jornadas miden lo mismo, así que el peso es el mismo: son las
    // horas operativas del día, no las 24 del calendario.
    weight: horas,
    primary: stripDot(d.toLocaleDateString(locale, { weekday: 'short' })),
    secondary: stripDot(d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })),
    highlight: d.toDateString() === hoyStr,
  }))
  const boundaries = Array.from({ length: 6 }, (_, i) => ((i + 1) / 7) * 100)

  const primero = dias[0]
  const ultimo = dias[6]
  const label = `${stripDot(primero.toLocaleDateString(locale, { day: 'numeric', month: 'short' }))} – ${stripDot(ultimo.toLocaleDateString(locale, { day: 'numeric', month: 'short' }))}`
  const stamp = `${primero.getFullYear()}-${String(primero.getMonth() + 1).padStart(2, '0')}-${String(primero.getDate()).padStart(2, '0')}`

  return construirTrack(
    'semana', dias, columns, boundaries, jornada, label, stamp,
    // 90 min son el 1,53 % del carril: cabe la hora, no el nº de carga. Ése va
    // en el `title` y en el popover.
    1.2,
    0.4,
    false, ahora,
  )
}

export function buildDockTrack(
  vista: DockVista,
  offset: number,
  i18n: TrackI18n,
  jornada: Jornada = JORNADA_POR_DEFECTO,
  ahora: number = Date.now(),
): DockTrack {
  return vista === 'dia'
    ? buildDayTrack(offset, i18n, jornada, ahora)
    : buildWeekHourTrack(offset, i18n, jornada, ahora)
}
